/* eslint-disable no-console */
import { client } from "./supabase";

export const TIPOS_PAGO_EMPLEADO = ["sueldo", "adelanto", "comision", "bono", "finiquito", "transporte"];
export const TIPOS_REQUIEREN_PERIODO = ["sueldo", "adelanto", "comision"];

export const getPagosEmpleados = async ({ pais, desde, hasta, empleadoId } = {}) => {
  try {
    let q = client
      .from("pagos_empleados")
      .select("*, empleados!inner(id, nombre, apellido, pais)")
      .order("fecha_pago", { ascending: false });
    if (pais) q = q.eq("empleados.pais", pais);
    if (desde) q = q.gte("fecha_pago", desde);
    if (hasta) q = q.lte("fecha_pago", hasta);
    if (empleadoId) q = q.eq("empleado_id", empleadoId);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getPagosEmpleados:", error);
    throw error;
  }
};

export const getPagosPorMovimientos = async (movIds = []) => {
  if (movIds.length === 0) return [];
  try {
    const { data, error } = await client.from("pagos_empleados").select("*").in("movimiento_bancario_id", movIds);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getPagosPorMovimientos:", error);
    throw error;
  }
};

export const getPagosEmpleadoMes = async (empleadoId, año, mes) => {
  try {
    const { data, error } = await client
      .from("pagos_empleados")
      .select("*")
      .eq("empleado_id", empleadoId)
      .eq("periodo_año", año)
      .eq("periodo_mes", mes);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getPagosEmpleadoMes:", error);
    throw error;
  }
};

// ─── Haberes imponibles por-mes (haberes_empleado) ──────────────────────────
// Bono one-off / comisión del mes / otros haberes atados a un período puntual (a diferencia de
// bonos_fijo/variable de la vigencia, que son fijos). Se pasan como `haberesMes` a
// calcularLiquidacionChile. Ver migración 20260730_haberes_empleado + FASE 3.1a/3.1b.

// Un empleado/mes → array {monto, imponible, tipo, concepto}. Puede haber varias filas (ej. la
// comisión de Nicolás llega desglosada por mes de facturación).
export const getHaberesEmpleadoMes = async (empleadoId, año, mes) => {
  try {
    const { data, error } = await client
      .from("haberes_empleado")
      .select("monto, imponible, tipo, concepto")
      .eq("empleado_id", empleadoId)
      .eq("periodo_año", año)
      .eq("periodo_mes", mes);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getHaberesEmpleadoMes:", error);
    throw error;
  }
};

// Batch por país (una sola query, sin N+1 por empleado-mes) — para FlujoCaja, que proyecta muchos
// meses. Trae empleado_id + período para indexar del lado del consumidor.
export const getHaberesPais = async (pais) => {
  try {
    let q = client
      .from("haberes_empleado")
      .select("empleado_id, periodo_año, periodo_mes, monto, imponible, tipo, concepto, empleados!inner(pais)");
    if (pais) q = q.eq("empleados.pais", pais);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getHaberesPais:", error);
    throw error;
  }
};

// ─── Override de fecha de pago del sueldo líquido (pago_sueldo_fecha) ────────
// Batch por país (patrón getHaberesPais, sin N+1). Devuelve las filas
// {empleado_id, periodo_año, periodo_mes, fecha_pago}; el consumidor (FlujoCajaV2.rrhhEntries) las
// indexa por empleado/período. Sin fila → la proyección usa el último día del mes. Ver FASE 8.1.
export const getFechasPagoPais = async (pais) => {
  try {
    let q = client
      .from("pago_sueldo_fecha")
      .select("empleado_id, periodo_año, periodo_mes, fecha_pago, empleados!inner(pais)");
    if (pais) q = q.eq("empleados.pais", pais);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getFechasPagoPais:", error);
    throw error;
  }
};

// Setea/actualiza el override de fecha de pago del sueldo (upsert sobre la UNIQUE
// empleado_id+periodo_año+periodo_mes). Sin override → la proyección usa el último día del mes.
export const setFechaPagoSueldo = async (empleadoId, año, mes, fecha) => {
  try {
    const { data, error } = await client
      .from("pago_sueldo_fecha")
      .upsert(
        { empleado_id: empleadoId, periodo_año: Number(año), periodo_mes: Number(mes), fecha_pago: fecha },
        { onConflict: "empleado_id,periodo_año,periodo_mes" }
      )
      .select();
    if (error) throw error;
    return data?.[0];
  } catch (error) {
    console.error("Error en setFechaPagoSueldo:", error);
    throw error;
  }
};

// Borra el override → la fila de sueldo vuelve al default (último día del mes).
export const eliminarFechaPagoSueldo = async (empleadoId, año, mes) => {
  try {
    const { error } = await client
      .from("pago_sueldo_fecha")
      .delete()
      .eq("empleado_id", empleadoId)
      .eq("periodo_año", Number(año))
      .eq("periodo_mes", Number(mes));
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error en eliminarFechaPagoSueldo:", error);
    throw error;
  }
};

// Alta de un haber (bono/comisión/otro). origen='manual' lo setea el caller (el motor usa
// 'motor_comision' en 3.2). Múltiples filas por empleado/mes permitidas.
export const crearHaberEmpleado = async (row) => {
  try {
    const { data, error } = await client.from("haberes_empleado").insert(row).select();
    if (error) throw error;
    return data?.[0];
  } catch (error) {
    console.error("Error en crearHaberEmpleado:", error);
    throw error;
  }
};

// Baja de un haber. Solo se debe invocar sobre filas origen='manual' (las 'motor_comision' las
// gestiona el motor); la UI no expone la acción en esas filas.
export const eliminarHaberEmpleado = async (id) => {
  try {
    const { error } = await client.from("haberes_empleado").delete().eq("id", id);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error en eliminarHaberEmpleado:", error);
    throw error;
  }
};

export const crearPagosEmpleado = async (rows = []) => {
  if (rows.length === 0) return [];
  try {
    const { data, error } = await client.from("pagos_empleados").insert(rows).select();
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en crearPagosEmpleado:", error);
    throw error;
  }
};

export const actualizarPagoEmpleado = async (id, cambios) => {
  try {
    const { data, error } = await client.from("pagos_empleados").update(cambios).eq("id", id).select();
    if (error) throw error;
    return data?.[0];
  } catch (error) {
    console.error("Error en actualizarPagoEmpleado:", error);
    throw error;
  }
};

export const eliminarPagoEmpleado = async (id) => {
  try {
    const { error } = await client.from("pagos_empleados").delete().eq("id", id);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error en eliminarPagoEmpleado:", error);
    throw error;
  }
};

export const eliminarPagosPorMovimiento = async (movId) => {
  try {
    const { error } = await client.from("pagos_empleados").delete().eq("movimiento_bancario_id", movId);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error en eliminarPagosPorMovimiento:", error);
    throw error;
  }
};

/**
 * Retorna un Set de empleado_id (uuid) que ya tienen al menos un pago tipo='finiquito'.
 */
export async function getEmpleadoIdsConFiniquito(pais) {
  try {
    let q = client.from("pagos_empleados").select("empleado_id, empleados!inner(pais)").eq("tipo", "finiquito");
    if (pais) q = q.eq("empleados.pais", pais);
    const { data, error } = await q;
    if (error) throw error;
    return new Set((data || []).map((p) => p.empleado_id));
  } catch (error) {
    console.error("Error en getEmpleadoIdsConFiniquito:", error);
    throw error;
  }
}

// Suma de lo ya pagado para un empleado en un período dado, solo tipos que restan del líquido devengado
export const sumarPagadoLiquido = (pagos, empleadoId, año, mes) =>
  pagos
    .filter(
      (p) =>
        p.empleado_id === empleadoId &&
        p.periodo_año === año &&
        p.periodo_mes === mes &&
        (p.tipo === "sueldo" || p.tipo === "adelanto")
    )
    .reduce((s, p) => s + Number(p.monto || 0), 0);
